import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useState } from 'react';
import { reportError } from '@/services/error-reporting-service';

export interface AddressFields {
  address1: string;
  city: string;
  state: string;
  zip: string;
}

export interface ValidationResult {
  isValid: boolean;
  suggestedAddress: AddressFields | null;
  addressesMatch: boolean;
}

/**
 * Normalizes an address into a standardized string format for comparison.
 * Trims each field, lowercases, and collapses multiple spaces into single spaces.
 */
export function normalizeAddress(address: AddressFields): string {
  const formatted = `${address.address1.trim()}, ${address.city.trim()}, ${address.state.trim()} ${address.zip.trim()}`;
  return formatted.toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Compares two addresses by normalizing and comparing their string representations.
 */
export function areAddressesEqual(addr1: AddressFields, addr2: AddressFields): boolean {
  return normalizeAddress(addr1) === normalizeAddress(addr2);
}

/**
 * Parses a Google Geocoder API result into our AddressFields format.
 *
 * Google's Geocoder API documentation:
 * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingResults
 *
 * The response structure includes address_components, where each component has:
 * - types: array of strings indicating the type (e.g., 'street_number', 'route', 'locality')
 * - short_name: abbreviated form (e.g., 'CA' for California)
 * - long_name: full form (e.g., 'California')
 *
 * We use the first type in the array as the primary key and prefer short_name for consistency.
 *
 * @returns AddressFields if all required fields are present, null otherwise (indicates
 *          the geocoder response couldn't be parsed into a valid address)
 */
export function parseGeocoderResult(result: google.maps.GeocoderResult): AddressFields | null {
  if (!result.address_components) return null;

  // Build a map of component types to their short names
  const components = result.address_components.reduce(
    (acc, cur) => {
      return {
        ...acc,
        [cur.types[0]]: cur.short_name,
      };
    },
    {} as Record<string, string>
  );

  const streetNumber = components.street_number;
  const route = components.route;
  // Combine street number and route if both exist, otherwise use route
  const address1 = streetNumber && route ? `${streetNumber} ${route}` : route;
  const city = components.locality || components.sublocality_level_1;
  const state = components.administrative_area_level_1;
  const zip = components.postal_code;

  // Return null if any required field is missing (all falsy values handled here)
  if (!address1 || !city || !state || !zip) {
    return null;
  }

  return {
    address1,
    city,
    state,
    zip,
  };
}

export function useGoogleAddressValidation() {
  // geocoding will be undefined until the Google Maps library is loaded
  const geocoding = useMapsLibrary('geocoding');
  const [isValidating, setIsValidating] = useState(false);

  const validateAddress = useCallback(
    async (address: AddressFields): Promise<ValidationResult> => {
      // If Google Maps geocoding library hasn't loaded yet, return invalid state
      if (!geocoding) {
        return {
          isValid: false,
          suggestedAddress: null,
          addressesMatch: false,
        };
      }

      setIsValidating(true);

      try {
        const geocoder = new geocoding.Geocoder();
        const addressString = normalizeAddress(address);

        const response = await geocoder.geocode({
          address: addressString,
          componentRestrictions: { country: 'US' },
        });

        // No results means Google couldn't find/validate the address
        if (!response.results || response.results.length === 0) {
          return {
            isValid: false,
            suggestedAddress: null,
            addressesMatch: false,
          };
        }

        const suggestedAddress = parseGeocoderResult(response.results[0]);

        // If we can't parse the result into our format, consider it a match
        // (the address was valid enough for Google, we just can't extract components)
        if (!suggestedAddress) {
          return {
            isValid: true,
            suggestedAddress: null,
            addressesMatch: true,
          };
        }

        const addressesMatch = areAddressesEqual(address, suggestedAddress);

        return {
          isValid: true,
          // Only return suggestedAddress if it differs from what the user provided
          // null means "no suggestion needed" (not an error)
          suggestedAddress: addressesMatch ? null : suggestedAddress,
          addressesMatch,
        };
      } catch (error) {
        // Log for Sentry/client-side error tracking
        // These errors could include: network failures, API quota exceeded, invalid API key
        reportError({ error });
        return {
          isValid: false,
          suggestedAddress: null,
          addressesMatch: false,
        };
      } finally {
        // Always clear the validating state
        setIsValidating(false);
      }
    },
    [geocoding]
  );

  return { validateAddress, isValidating };
}
